import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ReduxService } from 'dashboard-services/services'

import groupDialogService from '../groupDialogService'

export default (() => {
  const FIRST = 1
  const SECOND = 2
  const EMPTY_ARRAY = []

  const toSharedWith = items => items
    .map(item => ReduxService.updateObject(item, { value: item.email, label: item.email }))
    .sort((a, b) => String(a.email).localeCompare(String(b.email)))

  const useGroupDialogShare = ({ groupName, sharedWith: loadedSharedWith }) => {
    const [step, setStep] = useState(FIRST),
          [isInitialized, setIsInitialized] = useState(false),
          [defaultSharedWith, setDefaultSharedWith] = useState(EMPTY_ARRAY),
          [sharedWith, setSharedWith] = useState(EMPTY_ARRAY),
          [selectedUsers, setSelectedUsers] = useState(EMPTY_ARRAY),
          [addUserSelected, setAddUserSelected] = useState(EMPTY_ARRAY),
          secondStepRef = useRef(),

          editSelectedUsers = useCallback(() => {
            setAddUserSelected(selectedUsers)
            setStep(SECOND)
          }, [selectedUsers]),
          removeSelectedUsers = useCallback(() => {
            setSharedWith(sharedWith => sharedWith.filter(s => !selectedUsers.find(su => su?.email === s?.email)))
            setSelectedUsers(EMPTY_ARRAY)
          }, [selectedUsers]),
          onUserCheck = useCallback((_, { name }) => setSelectedUsers(selectedUsers => {
            const index = selectedUsers.findIndex(sel => sel?.email === name),
                  sharedWithObj = sharedWith.find(sw => sw.email === name)
            return index === -1 ? selectedUsers.add(sharedWithObj || { email: name, value: name }) : selectedUsers.remove(index)
          }), [sharedWith]),
          onAccessChange = useCallback((access, email) => {
            setSharedWith(sharedWith => sharedWith.updateInList({ access }, sharedWith.findIndex(s => s?.email === email)))
          }, []),
          onAddUserClose = useCallback(() => {
            if(addUserSelected.length > 0) {
              setStep(SECOND)
              setSelectedUsers(addUserSelected)
            }
          }, [addUserSelected]),
          onAddUserChange = useCallback((_, { data }) => {
            setAddUserSelected((data || EMPTY_ARRAY).map(el => ReduxService.updateObject(el, { email: el?.value })))
          }, []),
          goBack = useCallback(() => {
            setAddUserSelected(EMPTY_ARRAY)
            setSelectedUsers(EMPTY_ARRAY)
            setStep(FIRST)
          }, []),
          addUsers = useCallback(() => {
            const secondStep = secondStepRef.current,
                  newUsers = (secondStep?.getUsers() || EMPTY_ARRAY).map(newUser => ReduxService.updateObject(
                    newUser,
                    {
                      email: newUser.value,
                      access: secondStep.getAccess(),
                      sendMessage: secondStep.shouldSendMessage(),
                      message: secondStep.getMessage(),
                      dataStart: secondStep.getDataStart(),
                      dataEnd: secondStep.getDataEnd(),
                      timeZone: secondStep.getTimezone()
                    }
                  ))
            setSharedWith(sharedWith => newUsers.reduce((prev, preparedNewUser) => {
              const index = prev.findIndex(sw => preparedNewUser.email === sw.email)
              return index > -1 ? prev.replace(preparedNewUser, index) : prev.add(preparedNewUser)
            }, sharedWith))
            setAddUserSelected(EMPTY_ARRAY)
            setSelectedUsers(EMPTY_ARRAY)
            setStep(FIRST)
          }, []),
          shareChanges = useMemo(() => groupDialogService.getShareChanges({ defaultSharedWith, sharedWith, groupName }), [defaultSharedWith, groupName, sharedWith])

    useEffect(() => {
      if(!isInitialized && loadedSharedWith) {
        setDefaultSharedWith(loadedSharedWith)
        setSharedWith(toSharedWith(loadedSharedWith))
        setIsInitialized(true)
      }
    }, [isInitialized, loadedSharedWith])

    return useMemo(() => ({
      step,
      isInitialized,
      defaultSharedWith,
      sharedWith,
      selectedUsers,
      addUserSelected,
      secondStepRef,
      onUserCheck,
      onAccessChange,
      onAddUserChange,
      onAddUserClose,
      editSelectedUsers,
      removeSelectedUsers,
      addUsers,
      goBack,
      shareChanges
    }), [addUserSelected, addUsers, defaultSharedWith, editSelectedUsers, goBack, isInitialized, onAccessChange, onAddUserChange, onAddUserClose, onUserCheck, removeSelectedUsers, selectedUsers, shareChanges, sharedWith, step])
  }

  return {
    FIRST,
    SECOND,
    useGroupDialogShare
  }
})()
